import React, { useCallback, useRef, useSyncExternalStore } from 'react';
import { formatCurrency } from '@/utils/money';
import { orderGuideItemCollection } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/collections/order-entry-collection';
import { Typography } from '@usepepper/ui/atoms/typography';
import { usePricingStore } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/stores/pricing-store';
import { useTheme } from '@/contexts/theme-context';

interface OrderEntryTableGpDollarCellProps {
  grossProfitDollarDisplayString: string;
  variantPackUUID: string;
}

/**
 * Reactive GP$ cell that subscribes to PriceEditor state. When the user
 * edits a price, this cell recalculates GP$ in real-time via the PriceEditor.
 * Falls back to the static display string when not actively editing.
 */
export const OrderEntryTableGpDollarCell =
  React.memo<OrderEntryTableGpDollarCellProps>(
    ({ grossProfitDollarDisplayString, variantPackUUID }) => {
      const theme = useTheme();
      const editor = usePricingStore.getState().editors.get(variantPackUUID);

      const editorStateRef = useRef(editor?.getState() ?? null);
      const editorState = useSyncExternalStore(
        useCallback(
          (cb: () => void) =>
            editor?.subscribe((newState) => {
              editorStateRef.current = newState;
              cb();
            }) ?? (() => {}),
          [editor],
        ),
        useCallback(() => editorStateRef.current, []),
      );

      let displayValue = grossProfitDollarDisplayString;

      if (editor && editorState) {
        const gpAmount = editor.getGrossProfitInMicros();
        if (gpAmount !== null) {
          const currency =
            orderGuideItemCollection.get(variantPackUUID)?.variant_pack_info
              ?.currency;
          displayValue = formatCurrency(gpAmount, currency);
        }
      }

      return (
        <Typography color={theme.sk.color.text.primary} variant="paragraph-md">
          {displayValue}
        </Typography>
      );
    },
  );
